import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Steps,
  Switch,
  message,
} from 'antd';
import clsx from 'clsx';
import {
  filter,
  findIndex,
  includes,
  keys,
  map,
  omit,
  reverse,
  values,
} from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../AppContext';
import {
  EditButton,
  MinusCircle,
  WrongIcon,
} from '../../../../../../assets/svg';
import {
  ACCESS_TYPE,
  ADD_AGENCY_STEPS,
  AGENCY_TYPE_KEYS,
  BREAKPOINTS,
  GA_EVENT,
  GA_LABEL,
  PROJECT_CHECKLIST_STATUS,
} from '../../../../../../common/constants';
import { Event } from '../../../../../../common/trackEvents';
import useRouter from '../../../../../../common/useRouter';
import { formValidatorRules, titleCase } from '../../../../../../common/utils';
import CommonCard from '../../../../../../components/CommonCard';
import CommonDropdown from '../../../../../../components/CommonDropdown';
import CommonSelect from '../../../../../../components/CommonSelect';
import CommonTooltip from '../../../../../../components/CommonTooltip';
import EllipsisText from '../../../../../../components/EllipsisText';
import HasAccess from '../../../../../../components/HasAccess';
import AddContactModal from '../../../../../agencies/contacts/AddContactModal';
import {
  CREATE_PROJECT_AGENCY,
  UPDATE_PROJECT_AGENCY,
} from '../../../../graphql/Mutation';
import {
  GET_AGENCY_CONTACTS_DROPDOWN_LIST,
  GET_PROJECT_AGENCY_DROPDOWN_LIST,
  GET_PROJECT_AGENCY_EQC_TYPES_LIST,
} from '../../../../graphql/Queries';

const allContact = [];
const AddAgencyModal = (props) => {
  const {
    showModal,
    setShowModal,
    isUpdate,
    agencyData,
    setAgencyData,
    refetchAgencyData,
  } = props;
  const isTabletViewport = useMedia(`(min-width: ${BREAKPOINTS.tablet}px)`);
  const { getCurrentUser } = useContext(AppContext);
  const currentUser = getCurrentUser();
  const {
    params: { projectId },
  } = useRouter();
  const [form] = Form.useForm();
  const { required, characterWithoutWhiteSpace } = formValidatorRules;
  const [agencyId, setAgencyId] = useState(agencyData?.agencyId);
  const hasInspectionAccess = !!HasAccess({ type: ACCESS_TYPE.INSPECTION });
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [isSelectAllContacts, setIsSelectAllContacts] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [current, setCurrent] = useState(0);
  const [step1formDetails, setStep1formDetails] = useState();
  const [userList, setUserList] = useState([]);
  const [userData, setUserData] = useState();
  const [showUserModal, setShowUserModal] = useState(false);
  const [isGlobal, setIsGlobal] = useState(true);
  const [allEqcType, setAllEqcType] = useState([]);
  const [allAgencies, setAllAgencies] = useState([]);
  const [selectedContact, setSelectedContact] = useState();
  const [alreadyAddedUser, setAlreadyAddedUser] = useState();

  const handleCancel = () => {
    setShowModal(false);
    setAgencyData();
    form.resetFields();
  };
  useEffect(() => {
    if (hasInspectionAccess) {
      form.setFieldsValue({
        projectEqcTypeIds: agencyData?.projectAgencyEqcs?.map(
          (eqcValue) => eqcValue?.id,
        ),
      });
    }

    if (agencyData) {
      form.setFieldsValue({
        agencyId: agencyData?.agency?.name,
        userIds: agencyData?.projectAgencyContacts?.map(
          (contactValue) => contactValue?.user?.id,
        ),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [createProjectAgency, { loading: updateLoading }] = useMutation(
    CREATE_PROJECT_AGENCY,
    {
      onError() {},
      onCompleted(res) {
        setAlreadyAddedUser(res?.createProjectAgency?.users);
        if (!res?.createProjectAgency?.users) {
          Event(GA_EVENT.ADD_PROJECT_AGENCY, {
            label: GA_LABEL.ADD_PROJECT_AGENCY,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          setAgencyData();
          form.resetFields();
          setShowModal(false);
          refetchAgencyData();
        }
      },
    },
  );

  const [updateProjectAgency, { loading: createLoading }] = useMutation(
    UPDATE_PROJECT_AGENCY,
    {
      onError() {},
      onCompleted(res) {
        setAlreadyAddedUser(res?.updateProjectAgency?.users);
        if (!res?.updateProjectAgency?.users) {
          Event(GA_EVENT.EDIT_PROJECT_AGENCY, {
            label: GA_LABEL.EDIT_PROJECT_AGENCY,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            project_id: projectId,
            project_agency_id: agencyData?.id,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          setAgencyData();
          form.resetFields();
          setShowModal(false);
          refetchAgencyData();
        }
      },
    },
  );

  const onAgencySubmitFinish = async (formValues) => {
    if (current === 0) {
      setStep1formDetails(formValues);
      setCurrent(1);
      return;
    }
    if (!isGlobal && !userList?.length > 0) {
      message.destroy();
      message.error('Please add at least one user!');
      return;
    }
    const newFormValues = {
      userIds: isSelectAllContacts ? [] : formValues?.userIds,
      projectEqcTypeIds: isSelectAll ? [] : step1formDetails?.projectEqcTypeIds,
      selectedAllContacts: isSelectAllContacts,
      selectedAllEqcTypes: isSelectAll,
      contacts: userList,
    };
    let variables = isUpdate
      ? {
          data: {
            ...newFormValues,
            useExistingUser: !!alreadyAddedUser,
          },
          id: agencyData?.id,
        }
      : {
          ...formValues,
          agencyId: step1formDetails?.agencyId,
          agency: {
            name: step1formDetails?.agencyName,
            type: step1formDetails?.type,
          },
          ...newFormValues,
        };
    if (!hasInspectionAccess) {
      if (isUpdate) {
        variables.data = omit(variables?.data, ['projectEqcTypeIds']);
      } else {
        variables = omit(variables, ['projectEqcTypeIds']);
      }
    }
    if (isGlobal) {
      variables = omit(variables, ['agency']);
    }
    try {
      if (isUpdate) {
        await updateProjectAgency({
          variables: { ...variables },
        });
        return;
      }
      createProjectAgency({
        variables: {
          data: {
            ...variables,
            useExistingUser: !!alreadyAddedUser,
            projectId,
          },
        },
      });
    } catch (error) {
      return error;
    }
  };
  const handleDeselect = () => {
    setIsSelectAll(false);
    form.setFieldsValue({ projectEqcTypeIds: [] });
  };
  useEffect(() => {
    if (isSelectAll) {
      form.setFieldsValue({ projectEqcTypeIds: ['all'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectAll]);
  const handleContactsDeselect = () => {
    setIsSelectAllContacts(false);
    form.setFieldsValue({ userIds: [] });
  };
  useEffect(() => {
    if (isSelectAllContacts) {
      form.setFieldsValue({ userIds: ['all'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelectAllContacts]);
  const handleAddUser = () => {
    setShowUserModal(true);
  };

  const handleEdit = (user) => {
    setShowUserModal(true);
    setUserData(user);
  };

  const handleCallback = (dataList) => {
    if (dataList?.projectAgencyEqcTypeDropdownList?.data?.length > 0) {
      setAllEqcType((prev) => [
        ...prev,
        ...dataList?.projectAgencyEqcTypeDropdownList?.data,
      ]);
    }
    if (dataList?.agencyDropdownList?.data?.length > 0) {
      setAllAgencies((prev) => [
        ...prev,
        ...dataList?.agencyDropdownList?.data,
      ]);
    }
  };

  const handleDropdownChange = (value, addExtraData) => {
    allContact.push(...value?.projectAgencyUserDropdownList?.data);
    if (addExtraData) {
      allContact.push(addExtraData);
    }
  };

  const step1Form = (
    <>
      {!isUpdate && (
        <Form.Item label="Pick From Global ">
          <Switch checked={isGlobal} onChange={setIsGlobal} />
        </Form.Item>
      )}
      {isGlobal ? (
        <Form.Item name="agencyId" label="Team" rules={[required]}>
          <CommonDropdown
            placeholder="Select Team"
            onChange={(e) => {
              setCurrentPage(0);
              setAgencyId(e);
              setIsSelectAllContacts(false);
              form.setFieldsValue({ userIds: [] });
            }}
            disabled={!!isUpdate}
            showSearch
            optionFilterProp="children"
            allowClear
            query={GET_PROJECT_AGENCY_DROPDOWN_LIST}
            variables={{
              filter: { projectId },
            }}
            fetchPolicy="network-only"
            responsePath="agencyDropdownList.data"
            valuePath="id"
            labelPath="name"
            optionKey="user"
            showExtraLoader
            callback={handleCallback}
            addExtraData={allAgencies}
          />
        </Form.Item>
      ) : (
        <>
          <Form.Item
            rules={[
              required,
              characterWithoutWhiteSpace,
              {
                max: 250,
                message: 'Name cannot be more than 250 characters',
              },
            ]}
            name="agencyName"
            label="Team"
          >
            <Input allowClear placeholder="Enter Team Name" />
          </Form.Item>
          <Form.Item
            rules={[{ required, message: 'Required' }]}
            name="type"
            label="Type"
          >
            <CommonSelect
              className="mr-3"
              placeholder="Select Type"
              allowClear
              options={[
                ...values(AGENCY_TYPE_KEYS).map((type) => {
                  return {
                    key: type,
                    value: type,
                    label: titleCase(type),
                  };
                }),
              ]}
            />
          </Form.Item>
        </>
      )}

      {hasInspectionAccess && (
        <Form.Item
          label={
            <div className="d-flex justify-between width-percent-100">
              <div>Checklist</div>
              {isSelectAll && (
                <div
                  className="text-primary pointer"
                  role="button"
                  tabIndex="0"
                  onClick={handleDeselect}
                >
                  Deselect All
                </div>
              )}
            </div>
          }
          name="projectEqcTypeIds"
          normalize={(value) => {
            if (value.includes('all')) {
              setIsSelectAll(true);
              return ['all'];
            }
            return value;
          }}
        >
          <CommonDropdown
            placeholder="Select Checklist"
            mode="multiple"
            disabled={isSelectAll}
            showSearch
            optionFilterProp="children"
            allowClear
            query={GET_PROJECT_AGENCY_EQC_TYPES_LIST}
            variables={{
              filter: {
                projectId: Number(projectId),
                status: PROJECT_CHECKLIST_STATUS?.PUBLISHED,
                projectAgencyId: agencyData?.id,
              },
            }}
            fetchPolicy="network-only"
            responsePath="projectAgencyEqcTypeDropdownList.data"
            valuePath="id"
            labelPath="name"
            isSelectedAll={isSelectAll}
            optionKey="eqc-type"
            hasSelectAll
            showExtraLoader
            callback={handleCallback}
            addExtraData={allEqcType}
          />
        </Form.Item>
      )}
      {!isGlobal && (
        <span className="text-danger note-text">
          Note: This team will also be added at global level.
        </span>
      )}
      <div className="form-buttons">
        <Button
          shape="round"
          type="primary"
          className="save-button"
          htmlType="submit"
          disabled={false}
        >
          Next
        </Button>
      </div>
    </>
  );
  const step2Form = (
    <>
      {isGlobal && (
        <>
          <Form.Item
            name="userIds"
            label={
              <div className="d-flex justify-between width-percent-100">
                <div>Select Global User</div>
                {isSelectAllContacts && (
                  <div
                    className="text-primary pointer"
                    role="button"
                    tabIndex="0"
                    onClick={handleContactsDeselect}
                  >
                    Deselect All
                  </div>
                )}
              </div>
            }
            normalize={(value) => {
              if (value.includes('all')) {
                setIsSelectAllContacts(true);
                return ['all'];
              }
              return value;
            }}
            rules={[
              {
                required: userList?.length < 1,
                message: 'This field is required!',
              },
            ]}
          >
            <CommonDropdown
              allowClear
              showSearch
              mode="multiple"
              disabled={isSelectAllContacts}
              placeholder="Select User"
              optionFilterProp="children"
              query={GET_AGENCY_CONTACTS_DROPDOWN_LIST}
              variables={{ filter: { projectId, agencyId } }}
              fetchPolicy="network-only"
              responsePath="projectAgencyUserDropdownList.data"
              valuePath="id"
              labelPath="name"
              optionKey="contact-users"
              currentPage={currentPage}
              useEffectDeps={[agencyId]}
              conditionToCheckBeforeQuery={!!agencyId}
              projectId={agencyId}
              isSelectedAll={isSelectAllContacts}
              hasSelectAll
              showArrow
              showExtraLoader
              onChange={(id) => {
                setSelectedContact(
                  filter(allContact, (value) => includes(id, value?.id)),
                );
              }}
              addExtraData={selectedContact}
              callback={handleDropdownChange}
            />
          </Form.Item>
          <Divider dashed />
        </>
      )}
      <div className="create-agency-title">Create User</div>
      {userList?.length > 0 &&
        map(userList, (user, index) => (
          <>
            <Row
              gutter={10}
              align="middle"
              justify="space-between"
              className="mt-15"
              key={index}
            >
              <Col span={22}>
                <Card className="user-card">
                  <Row justify="space-between" align="middle">
                    <Col span={20}>
                      <EllipsisText text={user?.name} />
                    </Col>
                    <Col span={2}>
                      <CommonTooltip
                        trigger="focus"
                        title="Edit"
                        placement="bottom"
                      >
                        <Button
                          shape="round"
                          className="b-0"
                          disabled={false}
                          icon={<EditButton height={28} width={28} />}
                          onClick={() => handleEdit(user)}
                        />
                      </CommonTooltip>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col span={2}>
                <Popconfirm
                  title="Are you sure you want to remove this user?"
                  onConfirm={() => {
                    userList?.splice(index, index + 1);
                    setAlreadyAddedUser();
                    setUserList([...userList]);
                  }}
                  okText="Yes"
                  okButtonProps={{
                    disabled: false,
                  }}
                  cancelButtonProps={{
                    disabled: false,
                  }}
                  cancelText="No"
                >
                  <CommonTooltip
                    trigger="focus"
                    title="Remove"
                    placement="bottom"
                  >
                    <Button
                      disabled={false}
                      className="b-0"
                      icon={<MinusCircle />}
                    />
                  </CommonTooltip>
                </Popconfirm>
              </Col>
            </Row>
            {filter(
              map(userList, 'email'),
              (data) => data?.toLowerCase() === user?.email?.toLowerCase(),
            )?.length > 1 ? (
              <Row>
                <div className="text-danger mt-5">Email ID already exists</div>
              </Row>
            ) : (
              filter(map(userList, 'phoneNo'), (data) => data === user?.phoneNo)
                ?.length > 1 && (
                <Row>
                  <div className="text-danger mt-5">
                    Phone number already exists
                  </div>
                </Row>
              )
            )}
          </>
        ))}
      <Button
        type="link"
        className="mt-15"
        icon={<PlusOutlined />}
        disabled={false}
        onClick={handleAddUser}
      >
        Add User
      </Button>
    </>
  );

  const renderFrom = {
    0: step1Form,
    1: step2Form,
  };

  return (
    <div id="add-edit-agency-modal">
      {showUserModal && (
        <AddContactModal
          showModal={showUserModal}
          setShowModal={setShowUserModal}
          agencyContactData={userData}
          isUpdate={!!userData}
          setAgencyContactData={setUserData}
          setUserList={setUserList}
          userList={userList}
          onlyGetContactData
          agencyId={findIndex(userList, userData)}
          handleOnComplete={() => {
            setAlreadyAddedUser();
          }}
        />
      )}
      <Modal
        maskClosable={false}
        centered
        form={form}
        open={showModal}
        onCancel={handleCancel}
        footer={null}
        closeIcon={<WrongIcon />}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('add-edit-agency-modal')
        }
      >
        <div
          className={clsx(
            'd-flex align-center',
            isTabletViewport && 'justify-center',
          )}
        >
          <Steps
            className="mb-24 mt-5"
            current={current}
            items={[
              ...map(reverse(keys(ADD_AGENCY_STEPS)), (stepItem) => {
                return {
                  key: stepItem,
                  title: ADD_AGENCY_STEPS[stepItem],
                  id: `step-${stepItem}`,
                };
              }),
            ]}
          />
        </div>
        <Form
          layout="vertical"
          form={form}
          onFinish={onAgencySubmitFinish}
          onValuesChange={() => {
            if (alreadyAddedUser) {
              setAlreadyAddedUser();
            }
          }}
        >
          {renderFrom[current]}
          {alreadyAddedUser && current === 1 && (
            <div className="existing-user-info">
              <h3>User Details Already Exist</h3>
              <p>The details you entered match below user’s existing record.</p>
              <div className="existing-user-info-div">
                {map(alreadyAddedUser, (item, index) => {
                  return (
                    <CommonCard
                      bordered
                      className={clsx(
                        'existing-user-info-card',
                        index > 0 && 'mt-10',
                      )}
                    >
                      <div>
                        <div>
                          <b>Name : </b> {item?.name || '-'}
                        </div>
                        <div>
                          <b>Email : </b> {item?.email || '-'}
                        </div>
                        <div>
                          <b>Mobile Number : </b>
                          {item?.phoneNo || '-'}
                        </div>
                      </div>
                      <p>Either keep same or update the existing record.</p>
                    </CommonCard>
                  );
                })}
              </div>
            </div>
          )}
          {current !== 0 && (
            <div className="form-buttons">
              <Button
                shape="round"
                className="cancel-button"
                onClick={() => {
                  if (current === 1) {
                    setCurrent(0);
                  } else {
                    handleCancel();
                  }
                }}
                disabled={false}
              >
                {current === 1 ? 'Previous' : 'Cancel'}
              </Button>
              {alreadyAddedUser?.length > 0 ? (
                <Button
                  disabled={false}
                  type="primary"
                  shape="round"
                  loading={updateLoading || createLoading}
                  onClick={() => {
                    form.submit();
                  }}
                >
                  Add Existing User
                </Button>
              ) : (
                <Button
                  shape="round"
                  type="primary"
                  className="save-button"
                  htmlType="submit"
                  loading={updateLoading || createLoading}
                >
                  {isUpdate ? 'Save' : 'Add'}
                </Button>
              )}
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default AddAgencyModal;
